import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Button,
    Typography,
    Grid,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Divider,
    CircularProgress,
    TextField,
    Backdrop,
} from "@mui/material";
import DynamicDeskLogo from "../assets/DD_LOGO_SCHWARZ.png";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/zustand";
import authApiService from "../services/api/AuthApiService";
import { useTranslation } from "react-i18next";

const ConfirmNewPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const entHash = queryParams.get("uid");

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Dialogs & loading
  const [dialogOpen, setDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Error state
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });

  // Open (validate first) confirmation dialog
  const openConfirmationDialog = () => {
    if (validateForm()) {
      setDialogOpen(true);
    }
  };

  // Close confirmation dialog
  const closeConfirmationDialog = () => setDialogOpen(false);

  // Validate form
  const validateForm = () => {
    const tempErrors = {};

    if (!firstName.trim()) {
      tempErrors.firstName = t("firstNameRequired");
    }

    if (!lastName.trim()) {
      tempErrors.lastName = t("lastNameRequired");
    }

    if (!password) {
      tempErrors.password = t("passwordRequired");
    }

    if (!confirmPassword) {
      tempErrors.confirmPassword = t("confirmPasswordRequired");
    } else if (password !== confirmPassword) {
      tempErrors.confirmPassword = t("passwordsDoNotMatchError");
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Handle confirm -> submit form
  const handleSubmit = async () => {
    closeConfirmationDialog();
    setLoading(true);

    const updateObject = {
      firstName,
      lastName,
      password,
      confirmPassword,
      entHash,
    };

    try {
      await authApiService.updateNewUserPassword(updateObject);
      // Show success dialog
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error("Error updating password:", error);
      alert(t("updateError"));
    } finally {
      setLoading(false);
    }
  };

  // Success dialog -> navigate home
  const handleSuccessClose = async () => {
    setSuccessDialogOpen(false);
    setLoading(true);

    try {
      // 1. Fetch user/session data from backend
      const userData = await authApiService.getAuthFlags(); // same as normal login check

      // 2. Update auth store
      useAuthStore.getState().setAuthData({
        name: userData.name || "",
        email: userData.email || "",
        isPaid: userData.isPaid || false,
        isEmailVerified: userData.isEmailVerified ?? false,
        current_product: userData.current_product || null,
        subscribed_products: userData.subscribed_products || [],
        preferredLanguage: userData.preferredLanguage || "de",
        onboarding_completed: userData.onboarding_completed || false,
        first_login: false, // they just confirmed password
      });

      // 3. Navigate based on product type
      // physio_user bypasses isPaid and onboarding glag check
      if (userData.current_product === "physio_user") {
        navigate("/home/library", { replace: true });
      } else if (!userData.isPaid) {
        navigate("/checkout", { replace: true });
      } else if (!userData.onboarding_completed) {
        navigate("/onboarding-exercises", { replace: true });
      } else {
        navigate("/home/calendar", { replace: true });
      }
    } catch (error) {
      console.error("Auth fetch after password confirm failed:", error);
      // fallback navigation
      navigate("/checkout", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ height: "105vh", marginTop: "60px" }}>
      {/* Fullscreen Loader */}
      <Backdrop open={loading} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress size={80} />
      </Backdrop>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen}>
        <DialogTitle>{t("confirmHeading")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("confirmUpdateMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmationDialog}>{t("cancel")}</Button>
          <Button onClick={handleSubmit} color="primary">
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen}>
        <DialogTitle>{t("success")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("passwordUpdatedSuccess")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <div
        className="white-background"
        style={{ width: "100%", height: "82%" }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            padding: 3,
            height: "100%",
            justifyContent: "center",
            alignContent: "center",
            overflowY: "auto",
          }}
        >
          <Grid item xs={12} md={5}>
            <img
              src={DynamicDeskLogo}
              style={{ width: "124px", height: "124px", marginLeft: "0px" }}
              alt="Feedback"
            />
            <Typography
              variant="h4"
              style={{ marginTop: "42px", color: "#063bd0" }}
              gutterBottom
            >
              <b>{t("newAccount")}</b>
            </Typography>
            <Typography
              variant="h6"
              style={{ marginTop: "18px", color: "#063bd0" }}
            >
              {t("pleaseUpdateUserDetails")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={1}>
            <Divider
              orientation="vertical"
              sx={{ width: "2px", height: "100%", bgcolor: "divider" }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h5" style={{ color: "#063bd0" }} gutterBottom>
              <b>{t("confirmNewPasswordTitle")}</b>
            </Typography>

            <Grid container spacing={3} sx={{ marginTop: "10px" }}>
              {/* First Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("firstName")}
                  variant="outlined"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName}
                  autoComplete="off"
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("lastName")}
                  variant="outlined"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName}
                  autoComplete="off"
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("password")}
                  type="password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                  autoComplete="new-password"
                />
              </Grid>

              {/* Confirm Password */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("confirmPassword")}
                  type="password"
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword}
                  autoComplete="new-password"
                />
              </Grid>

              {/* Proceed Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={openConfirmationDialog}
                  sx={{
                    backgroundColor: "#063bd0",
                    paddingY: 1.4,
                    fontSize: "16px",
                  }}
                >
                  {t("proceed")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider
          orientation="vertical"
          flexItem
          style={{ width: "2px", marginRight: "-2px" }}
        />
      </div>

      {/* Success Dialog is intentionally not duplicated here; handled above */}
    </Container>
  );
};

export default ConfirmNewPassword;
